/**
 * The week ahead for every plant in the garden, as the outlook pipeline
 * (Trellis) sees it: each plant's verdicts by day and night, its watering,
 * and the weather they were judged on.
 *
 * Endpoint: GET /plants/outlook?zip=&tempUnit= → PlantOutlookResponse, the
 * whole garden in one answer. Graded by the backend
 * (services/grow/plantWeek, gardenOutlookAggregator); the app draws what it
 * is sent and grades nothing itself. A field marked optional is left out by
 * the server on purpose: when the forecast could not be had (forecastFailed),
 * the answer has no grading or observed.
 *
 * Who uses it: shared/hooks/usePlantOutlook (the one query) and the plant
 * list, Today and weather screens that read its types. Caching:
 * [PLANT_OUTLOOK_QUERY_KEY, zip, tempUnit] ('plant-outlook'),
 * OUTLOOK_STALE_TIME_MS (30 minutes); any change to the garden's plants or
 * stages invalidates 'plant-outlook'. Auth: the signed-in token, in the
 * garden being tended (X-Grow-Garden).
 * In a shared garden no ?zip is sent (gardenMembers.gardenZip), so the
 * backend reads the garden at its owner's postcode.
 *
 * @module api/outlook
 */

import { apiClient } from './apiClient';
import { gardenZip } from './gardenMembers';

/** How the week looks for a plant overall. */
export type OutlookState = 'all_clear' | 'take_care' | 'risk' | 'alert';

/** Whether the plant needs watering beyond what the forecast will deliver. */
export type WateringStatus = 'needs_water' | 'covered_by_rain' | 'surplus' | 'excess_rain';

/** Inch values arrive as strings from the pipeline; parse before arithmetic. */
export interface OutlookWatering {
  /**
   * From the backend's outlook (gardenOutlookAggregator
   * buildUnifiedWateringObject): needs_water when rain leaves a shortfall,
   * excess_rain when the forecast rain is more than maxWeeklyInches, surplus
   * when it is well over the need, covered_by_rain otherwise.
   */
  status: WateringStatus;
  /** What the plant needs this week: its water profile's weekly need, with the season's and hot days' modifiers. */
  neededInches: string | null;
  /** What rain is expected to supply: the forecast's rain, summed by day over the week. */
  forecastInches: string | null;
  /** The shortfall the gardener has to make up. */
  additionalInches: string | null;
  /** The outlook's own note on the week's water, in English. */
  notes: string | null;
  /**
   * The most rain a week it can take. From the plant's water profile:
   * water_profile_templates (by plant type) with water_profile_overrides (by
   * group, plant or variety), else crop_water_data.max_weekly_rain.
   */
  maxWeeklyInches: string | null;
  /** Rain in one day past which it is a downpour; from the same water profile. */
  heavyDayInches: string | null;
  /** Hours it can stand in water before its roots suffer: crop_water_data.standing_water_tolerance_hours. */
  standingWaterHours: number | null;
  /** The week's watering, or null when none is needed. */
  plan: WateringPlan | null;
  /**
   * The soil the week started from: the rain kept at the garden over the
   * last days and the waterings ticked on Today, less what the plant used
   * (backend services/grow/soilBalance). Null with no kept weather (the
   * week is planned from dry soil).
   */
  soil: OutlookSoil | null;
}

/** The soil a plant's week started from, from real rain and ticked waterings. */
export interface OutlookSoil {
  /** Water in the soil at the start of today, inches. */
  inches: number;
  /** Rain kept over the days looked back, inches. */
  rainInches: number;
  /** Days ticked as watered over those days. */
  watered: number;
  /** The first day looked back, "YYYY-MM-DD". */
  from: string;
}

/** One past day's weather at the garden's ZIP, as kept (backend observedWeatherService). */
export interface ObservedDay {
  /** "YYYY-MM-DD", the station's local day. */
  date: string;
  /** Rain and melted snow, inches; null when the source gave none. */
  precipIn: number | null;
  /** The day's high, Fahrenheit. */
  highF: number | null;
  /** The day's low, Fahrenheit. */
  lowF: number | null;
}

/** The weather that fell at the garden before today. */
export interface OutlookObserved {
  /** The garden's today, "YYYY-MM-DD" (the forecast's first day). */
  today: string;
  /** The kept days before it, oldest first; a day not kept is absent. */
  days: ObservedDay[];
}

/**
 * The week's watering as the backend plans it: the shortfall split into
 * deep waterings on dry days, as the crop's own water data prefers.
 */
export interface WateringPlan {
  /** How many waterings: 1, 2, or 3. */
  times: number;
  /** How long each one runs, in minutes. */
  minutes: number;
  /** The shortfall they make up, in inches. */
  inches: number;
  /** The dates to water, "YYYY-MM-DD". */
  days: string[];
  /** False for a crop that prefers a little and often: its waterings are not "deep". */
  deep: boolean;
}

/**
 * An icon Trellis has chosen for a condition.
 *
 * Resolved on the server from the configured icon pack, so the app draws what
 * the rest of the product draws rather than picking something that seems to
 * fit. The default pack is emoji, hence a value rather than a URL.
 */
export interface TrellisIcon {
  /** The icon pack's kind of value ('emoji' in the default pack). */
  type: string;
  /** The icon itself: an emoji in the default pack. */
  value: string;
}

/** One piece of guidance, already scoped to day or night. */
export interface OutlookAdvice {
  /** Which axis this speaks to — drives the icon, not the layout. */
  kind: 'temperature' | 'water';
  /** The reason code, e.g. CARE_COOL or needs_water. */
  code: string;
  /** e.g. "MOST DAYS: quite warm" */
  label: string;
  /** Whether it speaks of the days, the nights, or neither (water). */
  timeOfDay: 'day' | 'night' | null;
  /** The days it applies to, as English day names ("Tuesday"). */
  days: string[];
  /** The advice itself, one action per sentence. */
  sentences: string[];
  /** The icon Trellis has defined for this code, or null if it has none. */
  icon: TrellisIcon | null;
  /**
   * Palette name for this verdict, from Trellis.
   *
   * Colour means direction — cold conditions in cold colours — and how deep
   * the colour runs is what says how severe. Resolve it through the app's
   * palette; it is a name, not a class.
   */
  colorKey: string | null;
}

/**
 * One day's verdicts, kept apart by when they apply.
 *
 * Trellis classifies a day and its night separately, each with exactly one
 * temperature verdict. Merging them produced rows reading "Cooler than ideal,
 * Too cold" — a chilly afternoon and a cold night with nothing to say which
 * was which.
 */
export interface DayNightReasons {
  /** The daytime's reason codes. */
  day: string[];
  /** The night's reason codes. */
  night: string[];
}

/** One day's verdict for a plant. */
export interface OutlookDay {
  /** "YYYY-MM-DD", the garden's own day. */
  date: string;
  /** Weekday name, e.g. "Thursday". */
  label: string;
  state: OutlookState;
  /** Reason codes, e.g. CARE_WARM, CARE_HEAT, PROTECT_FROST. */
  reasons: DayNightReasons;
  /** Rain forecast for the day, in inches. Null when unknown. */
  rainInches: string | null;
  /** That day's watering verdict, which can differ from the week's. */
  waterStatus: WateringStatus | null;
  /** Why each half has its temperature verdict; null for a half with none. */
  because: { day: StressBecause | null; night: StressBecause | null };
}

/**
 * Why a day or night has its temperature verdict, from the backend's grading
 * (services/grow/plantStress). Temperatures are Fahrenheit.
 */
export interface StressBecause {
  /**
   * moderate or large: 5–9°F or 10°F and more outside the range the plant
   * thrives in. chill: a night under 50°F for a warm or heat-loving crop.
   * snap or spike: a sudden, unusual drop or jump. frost, freeze, cold, heat:
   * a danger.
   */
  basis: 'moderate' | 'large' | 'chill' | 'snap' | 'spike' | 'frost' | 'freeze' | 'cold' | 'heat';
  /** How far the reading is outside the range the plant thrives in. */
  offF?: number;
  /** For a snap or spike: the reading against the average of the three before it. */
  changeF?: number;
  /** For a snap or spike: the reading against its date's normal. */
  vsNormalF?: number;
}

/** A plant's week on the sliding scale, best to worst. */
export type WeekLevel = 'thriving' | 'ok' | 'slowed' | 'stressed' | 'atRisk';

/** A day or a night of the week. */
export interface OutlookHalf {
  /** "YYYY-MM-DD". */
  date: string;
  when: 'day' | 'night';
}

/**
 * One thing a plant's week holds, as the backend reads it
 * (services/grow/plantWeek): the one reading every screen and the weather
 * warnings draw from.
 */
export interface OutlookMark {
  /** "danger-PROTECT_FROST", "harm-chill", "downpour", "excess_rain", "needs_water". */
  family: string;
  kind: 'danger' | 'harm' | 'water';
  /** For harm by temperature: chill, snap or spike. */
  basis?: string;
  /** The verdicts it stands for, by code and time of day, to find them in the advice. */
  refs: Array<{ code: string; timeOfDay: 'day' | 'night' | null }>;
  /** Temperature or water, and the code, for its icon. */
  iconKind: 'temperature' | 'water';
  code: string;
  /** The halves it falls on. */
  halves: OutlookHalf[];
  /** The days, for rain. */
  dates: string[];
  /** For downpours: the week's rain is too much as well. */
  tooMuchRain?: boolean;
}

/** The week ahead for one of the user's plants. */
export interface PlantOutlook {
  /** The plant this is for. */
  plantObjId: number;
  /** The variety the advice was written for. */
  varietyId: number | null;
  /** The DEFAULT variety the advice was authored for, not the user's cultivar. */
  varietyName: string | null;
  /** Its kind in one word: "tomato". */
  groupSingular: string | null;
  /** The same for several: "tomatoes", "basil". What Today names a job's plants by. */
  groupPlural: string | null;
  /** The worst graded day, as Trellis names states. The app reads week instead. */
  state: OutlookState | null;
  /** How many graded days are in each state. */
  dayCounts: Record<string, number> | null;
  /** How the plant is taking the week, graded from its temperatures. */
  week: WeekLevel;
  watering: OutlookWatering | null;
  /** Extra water a container-grown plant needs, e.g. 1.6 for 1.6x. */
  containerMultiplier: number | null;
  /** Each day's verdicts, in order. */
  daily: OutlookDay[];
  /** The week's guidance, one piece per condition and time of day. */
  advice: OutlookAdvice[];
  /** The week read once by the backend: dangers, harm, rain, water. */
  marks: OutlookMark[];
  /** The halves that only slow the plant. */
  slow: OutlookHalf[];
}

/** Garden-wide framing that applies to every plant. */
export interface OutlookSummary {
  /** e.g. "daytime highs are averaging 7°F warmer than normal" */
  normalsLine: string | null;
  /** The week's rain in one line, as the pipeline words it. */
  precipLine: string | null;
  /** Markers for when a verdict applies, from Trellis's own icon set. */
  icons: { day: TrellisIcon | null; night: TrellisIcon | null };
  /** Every verdict's icon, keyed by reason code, so a day row can show one. */
  reasonIcons: Record<string, TrellisIcon>;
  /** Every verdict's palette name, keyed by reason code. */
  reasonColors: Record<string, string>;
  /**
   * Trellis's own account of the week, one item per condition.
   *
   * The text names the plants affected, so anything showing a hazard has no
   * reason to count plants and compose a sentence of its own.
   */
  weeklyItems: Array<{
    /** The condition's kind. */
    type: string;
    title: string;
    text: string;
    /** The icon's key in Trellis's set, and the icon it resolves to. */
    iconKey: string;
    icon: TrellisIcon | null;
    /** Palette name, as OutlookAdvice.colorKey. */
    colorKey: string | null;
  }>;
}

/** One day of weather, with the normal it is measured against. */
export interface ConditionsDay {
  /** "YYYY-MM-DD", the garden's own day. */
  date: string;
  /** Weekday name, labelled in the garden's timezone by the pipeline. */
  label: string | null;
  /** The forecast high and low, in tempUnit. */
  max: number | null;
  min: number | null;
  /** Thirty-year NOAA normal for this date — what "usual" means here. */
  normalMax: number | null;
  normalMin: number | null;
  /** Departure from normal; positive is warmer than usual. */
  maxVariance: number | null;
  minVariance: number | null;
  /** Chance of precipitation, 0-100. */
  pop: number | null;
  /** Forecast precipitation, in inches. */
  precipIn: number | null;
  /** Average wind speed, as the forecast gives it. */
  windAvg: number | null;
  /** The forecast's own icon code. */
  iconCode: string | null;
  // The day's iconUrl and shortForecast are still sent but not read (W24 Job D): its sky's words and icon key are the
  // Today brief's (weatherWeek.days[].skyText, skyIcon).
}

/** The week in aggregate, forecast against normal. */
export interface ConditionsMetrics {
  /** Highs and lows across the week (Daytime: the highs; Night: the lows), in tempUnit. */
  hottestDaytime: number | null;
  coolestDaytime: number | null;
  avgDaytime: number | null;
  hottestNight: number | null;
  coolestNight: number | null;
  avgNight: number | null;
  /** The week's normals, averaged. */
  avgNormalMax: number | null;
  avgNormalMin: number | null;
  /** The average departure from normal; positive is warmer. */
  avgMaxAnomaly: number | null;
  avgMinAnomaly: number | null;
  /** The single most extreme departure, which the average hides. */
  extMaxAnomaly: number | null;
  extMinAnomaly: number | null;
  /** The week's forecast precipitation, in inches. */
  totalPrecip7d: number | null;
}

/**
 * The weather itself, already in the requested units.
 *
 * The pipeline fetches normals and computes each day's departure from them to
 * judge whether a plant is stressed. This carries that same working through, so
 * "is this normal?" is answered from the numbers the advice was derived from.
 */
export interface OutlookConditions {
  /** The unit every temperature here is in: the one asked for. */
  tempUnit: 'f' | 'c';
  /** Always inches. */
  precipUnit: 'in';
  /** Where the forecast and the normals came from, by name. */
  source: { weather: string | null; normals: string | null };
  /** The week, a day each. */
  days: ConditionsDay[];
  /** The week in aggregate. */
  metrics: ConditionsMetrics;
}

/**
 * Conditions right now, from the first period of the same forecast the week's
 * advice was built on — so the page is not showing two different retrievals.
 */
export interface OutlookCurrent {
  /** The NWS period's own name, e.g. "This Afternoon" or "Tonight" (compared with the weather context's period). */
  label: string | null;
  /** The period's name in the gardener's language, as the server says it ("Esta tarde"). */
  labelText: string | null;
  /** In the unit asked for. */
  temperature: number | null;
  /** The NWS's words ("Mostly Sunny"). */
  shortForecast: string | null;
  isDaytime: boolean | null;
  iconUrl: string | null;
  /** As the NWS words it ("5 to 10 mph"). */
  windSpeed: string | null;
  /** Relative humidity, percent. */
  humidity: number | null;
}

/**
 * The grading thresholds the outlook was graded by, set in the backend's Grow
 * settings panel. Fahrenheit. Sent with the outlook; the app says nothing
 * from them (audit batch 4: the server writes what they mean), so it keeps
 * no defaults of its own for them.
 */
export interface OutlookGrading {
  /** Under this, a night can injure a warm-season crop. */
  chillFloorF: number;
  /** How far outside where a plant thrives a reading is moderate. */
  moderateF: number;
  /** And large. */
  largeF: number;
  /** At or below this, frost can form on a frost-sensitive plant's leaves. */
  frostF: number;
  /** The crop classes the chilling line applies to. */
  chillSensitive: string[];
}

/** What GET /plants/outlook answers: the weather, now, and every plant's week. */
export interface PlantOutlookResponse {
  /** The server's flag. */
  success: boolean;
  /** The week's weather; null when the forecast could not be had. */
  conditions: OutlookConditions | null;
  /** The thresholds the weeks were graded by; absent when the forecast could not be had (forecastFailed). */
  grading?: OutlookGrading;
  /** Conditions now; null when unknown. */
  current: OutlookCurrent | null;
  /** The garden-wide framing; null when there is none. */
  summary: OutlookSummary | null;
  /** Every plant's week, one each (matched by plantObjId). */
  plants: PlantOutlook[];
  /** The weather that fell before today; null when none is kept; absent when the forecast could not be had. */
  observed?: OutlookObserved | null;
}

/**
 * Fetches the week's outlook for every plant the signed-in user has.
 *
 * One request covers the whole garden — the backend resolves each plant to the
 * variety its advice was written for and returns the results already matched to
 * plant_obj_id, so callers never re-derive the plant hierarchy.
 *
 * @param zip - The signed-in person's own postal code; sent only in their own garden (gardenZip).
 * @param tempUnit - Unit the narrative lines should be phrased in.
 * @returns The weather, now, and every plant's week.
 */
export async function getPlantOutlook(zip: string, tempUnit: 'f' | 'c' = 'f'): Promise<PlantOutlookResponse> {
  const response = await apiClient.get<PlantOutlookResponse>('/plants/outlook', {
    params: { zip: gardenZip(zip), tempUnit },
  });
  return response.data;
}
